import { AUTH_USER_KEY } from "./authSession";

const GENERIC_NAMES = new Set(["system", "unknown user", "employee"]);

const normalizeName = (value) => String(value || "").trim();

const normalizeEmployeeId = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const getStoredUserName = () => {
  try {
    const user = JSON.parse(localStorage.getItem(AUTH_USER_KEY) || "{}");
    return normalizeName(user?.name);
  } catch {
    return "";
  }
};

const isSpecificName = (value) => {
  const normalized = normalizeName(value);
  return Boolean(normalized) && !GENERIC_NAMES.has(normalized.toLowerCase());
};

const getLatestTimestamp = (activity) => {
  const parsed = new Date(activity?.timestamp).getTime();
  if (Number.isNaN(parsed)) return 0;
  return parsed;
};

export const buildEmployeeLookup = (employees = []) => {
  const lookup = new Map();

  for (const employee of Array.isArray(employees) ? employees : []) {
    const employeeId = normalizeEmployeeId(employee?.id);
    const employeeName = normalizeName(employee?.name);

    if (employeeId && employeeName) {
      lookup.set(employeeId, employeeName);
    }
  }

  return lookup;
};

export const resolveActivityEmployeeId = (activity = {}) =>
  normalizeEmployeeId(activity?.actorId) ||
  normalizeEmployeeId(activity?.employeeId) ||
  normalizeEmployeeId(activity?.targetEmployeeId) ||
  null;

export const resolveActivityActorName = (activity = {}, employeeLookup = new Map()) => {
  const candidateIds = [
    activity?.actorId,
    activity?.employeeId,
    activity?.targetEmployeeId,
  ]
    .map(normalizeEmployeeId)
    .filter(Boolean);

  for (const employeeId of candidateIds) {
    const employeeName = normalizeName(employeeLookup.get(employeeId));
    if (employeeName) return employeeName;
  }

  const textCandidates = [
    activity?.actorName,
    activity?.employeeName,
    activity?.targetEmployeeName,
    getStoredUserName(),
  ];

  for (const candidate of textCandidates) {
    if (isSpecificName(candidate)) return normalizeName(candidate);
  }

  for (const candidate of textCandidates) {
    const normalized = normalizeName(candidate);
    if (normalized) return normalized;
  }

  return "System";
};

export const resolveActivitySubjectName = (activity = {}, employeeLookup = new Map()) => {
  const candidateIds = [
    activity?.targetEmployeeId,
    activity?.employeeId,
    activity?.actorId,
  ]
    .map(normalizeEmployeeId)
    .filter(Boolean);

  for (const employeeId of candidateIds) {
    const employeeName = normalizeName(employeeLookup.get(employeeId));
    if (employeeName) return employeeName;
  }

  const textCandidates = [
    activity?.targetEmployeeName,
    activity?.employeeName,
    activity?.actorName,
    getStoredUserName(),
  ];

  for (const candidate of textCandidates) {
    if (isSpecificName(candidate)) return normalizeName(candidate);
  }

  return resolveActivityActorName(activity, employeeLookup);
};

export const groupActivitiesByEmployee = (activities = [], employeeLookup = new Map()) => {
  const grouped = new Map();

  for (const activity of Array.isArray(activities) ? activities : []) {
    const resolvedEmployeeId = resolveActivityEmployeeId(activity);
    const resolvedActorName = resolveActivityActorName(activity, employeeLookup);
    const resolvedSubjectName = resolveActivitySubjectName(activity, employeeLookup);
    const key =
      resolvedEmployeeId ||
      resolvedActorName.toLowerCase() ||
      resolvedSubjectName.toLowerCase() ||
      `activity-${activity?.id || activity?.timestamp || "unknown"}`;
    const normalizedActivity = {
      ...activity,
      resolvedEmployeeId,
      resolvedActorName,
      resolvedSubjectName,
    };
    const existing = grouped.get(key);

    if (!existing) {
      grouped.set(key, normalizedActivity);
      continue;
    }

    const currentTime = getLatestTimestamp(normalizedActivity);
    const existingTime = getLatestTimestamp(existing);

    if (currentTime > existingTime || (currentTime === existingTime && Number(activity?.id || 0) > Number(existing?.id || 0))) {
      grouped.set(key, normalizedActivity);
    }
  }

  return [...grouped.values()].sort((left, right) => {
    const timeDiff = getLatestTimestamp(right) - getLatestTimestamp(left);
    if (timeDiff !== 0) return timeDiff;
    return Number(right?.id || 0) - Number(left?.id || 0);
  });
};
